'use strict';

const ContentUtils = require('utils/ContentUtils'),
    {t} = require('services/i18nextWrapper'),
    ChronologycStore = require('stores/ChronologycStore'),
    TankStore = require('stores/TankStore'),
    DashboardLayoutStore = require('stores/DashboardLayoutStore'),
    DateUtils = require('utils/DateUtils'),
    OrderUtils = require('utils/OrderUtils'),
    EventEmitter = require('events').EventEmitter,
    DashboardModalUtils = require('components/DashboardModal/DashboardModalUtils'),
    DraftSchedule = require('pollers/DraftSchedule'),
    DashboardAction = require('../actions/DashboardActionCreators'),
    Notification = require('components/Notification/Notification'),
    assign = require('object-assign'),
    _ = require('lodash'),
    CHANGE_EVENT = 'change',
    propsToValiadte = ['x', 'y', 'i', 'h', 'batchNumber'],
    Constants = require('constants/AppConstants'),
    BrewhouseStore = require('stores/BrewhouseStore'),
    OrderSingleStore = require('stores/OrderSingleStore'),
    ActionTypes = require('constants/AppConstants').ActionTypes,
    AppDispatcher = require('dispatcher/SchedulerAppDispatcher'),
    DashboardServiceCache = require('services/DashboardServiceCache');


function isSiblingTankPositionAvailable(newPositionSiblingTank, differedDays) {
    return newPositionSiblingTank && !DashboardLayoutStore.isEmpty(newPositionSiblingTank) && differedDays !== undefined;
}

const OrderStore = assign({}, EventEmitter.prototype, {

    /**
     * The tracking last order created from this store
     */
    lastOrderCreated: null,
    /**
     * The tracking last order created from this store
     */
    lastOrderUpdated: null,

    /**
     * Handler of new position of order taking and move sticky orders (same batch number) if need it.
     * @param {Object} oldPosition
     * @param {Object} newPosition
     * @private
     */
    moveOrders(positionOld, positionNew) {
        try {
    
            if (ContentUtils.isNull(positionNew)) {
                return;
            }
    
            if (ContentUtils.isSameObject(positionOld, positionNew, propsToValiadte)) {
                // just in case that we call this method with the same position, do nothing
                return;
            }
    
            if (ChronologycStore.isPositionOutOfRange(positionNew)) {
                Notification.show(t('dashboard_notification_keep_scrolling_to_view_entire_order'), 'warning');
                DashboardAction.forceRenderDashboard(); // needed to get back the position after the second time to retry move wrong
                return;
            }
    
            if (DashboardLayoutStore.isInvalidMovement(positionNew, positionOld)) {
                // Enter here means the auto-located new position was wrong, due a Grid Layout problem
                // when you are resizing and order over an existing one, co-location it doesn't work properly.
                // means you don't be able to resize (change the `h` and also the `y` of a position at the same time.
                Notification.show(t('notification_the_position_is_taken'), 'warning');
                DashboardAction.forceRenderDashboard(); // needed to get back the position after the second time to retry move wrong
                return;
            }
    
            if (!this._isProperlyTypeOfTankDestination(positionNew)) {
                Notification.show(t('dashboard_notification_order_can_not_moved_differen_type_tank'));
                DashboardAction.forceRenderDashboard(); // needed to get back the position after the second time to retry move wrong
                return;
            }
    
            if (!OrderUtils.checkIfTankHaveEnoughVolume(positionNew)) {
                Notification.show(t('dashboard_notification_volume_capacity_insufficient'));
                DashboardAction.forceRenderDashboard(); // needed to get back the position after the second time to retry move wrong
                return;
            }
    
            let result;
            // calculate the new vertical position.
            const offsetY = positionNew.y - positionOld.y;
            const orderPosition = this.getSafeOrder(positionNew.i);
            const haveBatchs = _.get(orderPosition, 'batchs.length', 0) > 0;
    
            if (!ContentUtils.isBHBatch(positionNew.i) && haveBatchs && !BrewhouseStore.isEnoughVolumeInTank(orderPosition, positionNew.x)) {
                Notification.show(t('dashboard_notification_volume_capacity_insufficient'));
                DashboardAction.forceRenderDashboard();
                return;
            }
    
            if (ContentUtils.isBHBatch(positionNew.i) && haveBatchs) {
                // enter here means that move one batch from Brewhouse side and was a batch, otherwise if position is BH without batch is a single in BH section
                result = this._changeBatchPosition(positionOld, positionNew, offsetY);
            } else {
                // enter here means that move one order from Fermenter or Packaging side.
                if (offsetY === 0) {
                    // enter here means that was a horizontal movement, so is not need to change neither linked orders
                    result = this._changeOrderFromNewPosition(positionOld, positionNew);
                } else {
                    result = this._recalculateOrdersWithNewVerticalPosition(positionOld, positionNew, offsetY);
                }
            }
    
            AppDispatcher.dispatch({type: ActionTypes.FORCE_MANUAL_DRAWING_DASHBOARD});
    
            return result;
    
        } catch(error) {
            console.error('OrderStore.moveOrders: ', error);
            throw error;
        }
    },

    /**
     * Retrieve the last order updated
     * @return {*}
     */
    getLastOrderCreated() {
        return this.lastOrderCreated;
    },

    /**
     * Retrieve the last order updated
     * @return {*}
     */
    getLastOrderUpdate() {
        return this.lastOrderUpdated;
    },

    /**
     * Retrieve the next batch number available.
     * @return {*}
     */
    getNextBatchNumber() {
        const lastOrderCreated = this.lastOrderCreated || this.getOrders()
        .filter(o => o.dateCreated)
        .map(order => {
            order.batchNumberInt = +_.join((order.batchNumber || '1').match(/[\d]/gi), '');
            return order;
        })
        .sort((a, b) => a.batchNumberInt - b.batchNumberInt)
        .slice(-1)[0];

        if (!lastOrderCreated) {
            return '1';
        }

        const batchNumber = lastOrderCreated.batchNumber;
        const partNumber = _.join(batchNumber.match(/[\d]/gi), '');
        const partText = _.join(batchNumber.match(/[\D]/gi), '');

        if (batchNumber.indexOf(partNumber) > 0) {
            return partText + ((+partNumber) + 1);
        } else {
            return ((+partNumber) + 1) + partText;
        }
    },

    getLastComputedOrderUpdated(orders = []) {
        if (orders && orders.sort && orders.length) {
            return orders.sort(function (a, b) {
                return new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
            }).slice(-1)[0];
        }
    },

    getLastComputedOrderCreated(orders = []) {
        if (orders && orders.sort && orders.length) {
            return orders.sort(function (a, b) {
                return new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime();
            }).slice(-1)[0];
        }
    },

    getOrderByDateAndTank(date, tank) {
        const dateTarget = DateUtils.toDateText(date);
        const tankId = _.get(tank, 'id');
        return _.find(this.getOrders(), order => {
            let targetFounded = false;

            if (!order) return false;
            
            if (order.isSingle) {
                if (_.get(order, 'singleTankId') === tankId) {
                    targetFounded = DateUtils.isDateBetween(dateTarget, order.fromDate, order.toDate);
                } else {
                    targetFounded = false;
                }

                if (!targetFounded && order.batchs) {
                    // enter here means that dont found yet the order, so we need find by batch
                    targetFounded = _.some(order.batchs, batch => batch.turnId === tankId);
                }
            } else {
                if (_.get(order, 'fermenterTankId') === tankId) {
                    targetFounded = DateUtils.isDateBetween(dateTarget, order.brewDate, order.filtrationDate);
                } else if (_.get(order, 'briteTankId') === tankId) {
                    targetFounded = DateUtils.isDateBetween(dateTarget, order.filtrationDate, order.orderDate);
                } else {
                    targetFounded = false;
                }

                if (!targetFounded && order.batchs) {
                    // enter here means that dont found yet the order, so we need find by batch
                    targetFounded = _.some(order.batchs, batch => batch.turnId === tankId);
                }
            }

            return targetFounded;
        });
    },

    updateLastOrderUpdated(order) {
        this.lastOrderUpdated = order;
    },

    updateLastOrderCreated(order) {
        this.lastOrderCreated = order;
    },

    hydrateOrdersOutOfRange(layout, fromDashboard = ChronologycStore.getCurrentFrom(), toDashboard = ChronologycStore.getCurrentTo()) {
        // clean layout with error orders with errors
        let orders = _.filter(OrderUtils.getOrderByRange(fromDashboard, toDashboard), order => order && order.briteTank && order.fermenterTank || order.singleTank);

        // the current visible position of dashboard
        let visualOrdersPositionsIds = _.map(layout, l => ContentUtils.cleanId(l.i));
        let visualOrdersPositionWithOrder = _.map(layout, l => {
            return {
                position: l,
                order: _.find(orders, order => ContentUtils.cleanId(l.i) === order.id)
            };
        });

        // CASE 1 - filter order that are completly ouside of dashboard modal, (the most frequent case)
        let ordersCompletlyOutSide = _.filter(orders, order => visualOrdersPositionsIds.indexOf(order.id) === -1);
        let ordersCompletlyOutSidePositions = _.flatten(_.map(ordersCompletlyOutSide, order => OrderUtils.convertOrderToPositions(order, fromDashboard)));

        // CASE 2 - which order that not have sibling position
        let ordersWithoutSiblingIds = ContentUtils.getNonDuplicates(visualOrdersPositionsIds);
        let ordersWithoutSibling = _.filter(orders, order => ordersWithoutSiblingIds.indexOf(order.id) > -1);
        let ordersWithoutSiblingPositions = _.flatten(_.map(ordersWithoutSibling, order => OrderUtils.convertOrderToPositions(order, fromDashboard)));

        // CASE 3 - add sibling position in case of combined orders
        let ordersWithSiblingPositionsIds = ContentUtils.getDuplicates(visualOrdersPositionsIds);
        let ordersWithSiblingPositionsAndOrders = _.filter(visualOrdersPositionWithOrder, (positionWithOrder) => {
            return ordersWithSiblingPositionsIds.indexOf(ContentUtils.cleanId(positionWithOrder.position.i)) > -1;
        });

        // Complete the partially position shown
        let ordersWithSiblingPartiallyShown = _.filter(ordersWithSiblingPositionsAndOrders, OrderUtils.isOrderPartiallyShown.bind(this, fromDashboard, toDashboard));
        let ordersWithSiblingPartiallyShownPosition = _.flatten(_.map(ordersWithSiblingPartiallyShown, (positionAndOrder) => {
            return OrderUtils.convertOrderToPositions(positionAndOrder.order, fromDashboard, _.get(positionAndOrder, 'position'));
        }));

        // to Order
        let ordersWithSibling = _.map(ordersWithSiblingPartiallyShown, positionAndOrder => positionAndOrder.order);

        // Group all Ids of order of the orders that was hydrated
        let ordersToCalculate = _.map(_.concat(ordersCompletlyOutSide, ordersWithoutSibling, ordersWithSibling), order => order.id);

        // Add all brewhouse batch that are whiting current dashboard visible range
        const brewhousePosition = _.filter(layout, p => ContentUtils.isBHBatch(p.i));

        // Remove all the operation that we already process before in order to avoid duplications errors
        const ordersToCalculatePositions = _.filter(layout, l => {
            return _.indexOf(ordersToCalculate, ContentUtils.cleanId(l.i)) === -1;
        });

        const boxesRecalculated = _.concat(ordersCompletlyOutSidePositions, ordersWithoutSiblingPositions, ordersWithSiblingPartiallyShownPosition, brewhousePosition);
        let result = _.concat(ordersToCalculatePositions, boxesRecalculated);

        // remove all duplicate positions
        result = ContentUtils.removeDuplicateByAttribute(result, ['x', 'y']);

        return result;
    },

    /**
     * Given order, change or remove task if are necessary depend is dragging or resizing.
     * @param order
     * @param isDragging
     * @param offsetDays
     * @returns {Order | null}
     */
    recalculateOrderTasks(order, isDragging, offsetDays, offsetResizing, isBriteTank) {
        if (!order) {
            return null;
        }

        if (isDragging) {
            order = this._recalculateOrderTasksWhenDragging(order, offsetDays);
        } else {
            order = this._recalculateOrderTasksWhenResizing(order, offsetResizing, isBriteTank);
        }

        return order;
    },

    _recalculateOrderTasksWhenDragging(order, offsetDays) {
        const isDirectionTop = offsetDays < 0;
        let offsetDaysOfBrewDate;
        // Invert positive or negative depended of the direction.
        const newBrewDate = DateUtils.addDays(order.brewDate, offsetDays);

        offsetDaysOfBrewDate = DateUtils.getDaysBetween(order.brewDate, newBrewDate);
        offsetDaysOfBrewDate = isDirectionTop ? -Math.abs(offsetDaysOfBrewDate) : Math.abs(offsetDaysOfBrewDate);

        // All date tasks must be changes without need to remove any task.
        order.fermenterTasks = _.map(order.fermenterTasks, function (task) {
            task.date = DateUtils.toDateText(DateUtils.addDays(DateUtils.toDate(task.date), offsetDaysOfBrewDate));
            // update end dete or each product if needed
            task = OrderUtils.recalculateOffsetProductsEndDate(task, offsetDays);

            // Update task of each process
            task = OrderUtils.recalculateProcessDate(task, offsetDays);
            return task;
        });

        order.packagingTasks = _.map(order.packagingTasks, function (task) {
            task.date = DateUtils.toDateText(DateUtils.addDays(DateUtils.toDate(task.date), offsetDaysOfBrewDate));

            // update end dete or each product if needed
            task = OrderUtils.recalculateOffsetProductsEndDate(task, offsetDays);

            // Update task of each process
            task = OrderUtils.recalculateProcessDate(task, offsetDays);
            return task;
        });

        // compute the process batchs
        if (ContentUtils.isNotEmptyArray(order.batchs)) {
            order.batchs = order.batchs.map(batch => {
                batch = OrderUtils.recalculateProcessDate(batch, offsetDays);
                return batch;
            });
        }

        return order;
    },

    _recalculateOrderTasksWhenResizing(order) {
        // NOTICE: when enter here the `order` is already updated. So, the only we need to do is:
        // - Update the dates of type task of `FILTRATION_TASK` & `PACKAGING_TASK` type task.
        // - Remove duplicity of `SECONDARY_TASK` in favor of `FILTRATION_TASK` or `PACKAGING_TASK`.
        // - Remove `SECONDARY_TASK` which are out of range.

        order = this._updateDateAndTypeTask(order, 'filtrationDate', 'fermenterTasks', Constants.FILTRATION_TASK);
        order = this._updateDateAndTypeTask(order, 'orderDate', 'packagingTasks', Constants.PACKAGING_TASK);

        // Remove unneeded fermented tasks
        order.fermenterTasks = _.filter(order.fermenterTasks, function (task) {
            return DateUtils.isAfterOrEquals(task.date, order.brewDate) && DateUtils.isBeforeOrEquals(task.date, order.filtrationDate);
        });

        // Remove unneeded packaging tasks
        order.packagingTasks = _.filter(order.packagingTasks, function (task) {
            return DateUtils.isAfterOrEquals(task.date, order.filtrationDate) && DateUtils.isBeforeOrEquals(task.date, order.orderDate);
        });

        return order;
    },

    _updateDateAndTypeTask(order, dateField, tasksField, taskType) {
        const isCombinedOrder = order && !order.isSingle;
        if (isCombinedOrder) {
            // always search first by type task in order to return the old fermenter/packaging task which need to be updated
            const dateTarget = order[dateField];
            let existingTask = _.find(order[tasksField], task => task.date === dateTarget);

            if (existingTask) {
                // update the fermenting/packaging task in order to keep sticky to their fermenting/packaging dates.
                if (existingTask.type === taskType) {
                    // Enter here when you don't change the order, it's just the same dates, so
                    // it's not needed to do nothing.
                    return order;
                } else {
                    // replace the new task updated for the old task
                    existingTask.type = taskType;
                    order[tasksField] = ContentUtils.replace(order[tasksField], existingTask, 'date');
                }
            } else {
                // turn secondary previous primary
                let existingTask = _.find(order[tasksField], task => task.type === taskType);
                if (existingTask) {
                    existingTask.type = Constants.SECONDARY_TASK;
                    order[tasksField] = ContentUtils.replace(order[tasksField], existingTask, 'date');
                }
                // create new primary task
                const newTask = DashboardModalUtils.toEmptyTask(dateTarget);
                newTask.date = dateTarget;
                newTask.type = taskType;
                order[tasksField] = ContentUtils.pushSafe(order[tasksField], newTask);
            }
        }
        return order;
    },

    /**
     * Retrieve all cache orders.
     * @returns {*|Array}
     */
    getOrders() {
        return DashboardServiceCache.getOrders();
    },

    /**
     * Retrieve order by Id.
     * @param orderId
     * @returns {*}
     */
    getOrder(orderId) {
        return DashboardServiceCache.getOrder(orderId);
    },

    /**
     * Retrieve order by batch number.
     * @param orderId
     * @returns {*}
     */
    getOrderByBatchNumber(batchNumber) {
        return _.filter(DashboardServiceCache.getOrders(), order => order.batchNumber === batchNumber);
    },

    /**
     * Retrieve order by Id, and also with postfix like: '_BT', '_BH', etc
     * @param orderId
     * @return {*}
     */
    getSafeOrder(orderId) {
        return DashboardServiceCache.getSafeOrder(orderId);
    },

    /**
     * Given new order, find if already exist, so if really new we create a new one, if are existing one, we will change it.
     * @param {Object} order
     * @param {Function} onSuccess
     * @param {Function} onFailure
     * @param {Boolean} isBHAllowed
     */
    addOrder(order, onSuccess, onFailure, isBHAllowed) {
        if (order.id) {
            var orderRecovered = this.getOrder(order.id);
            const offsetY = OrderUtils.getVerticalOffeset(orderRecovered, order);
            if (orderRecovered) {
                this.changeOrder(order, offsetY, onSuccess, onFailure, isBHAllowed);
            } else {
                this.addNewOrder(order, onSuccess, onFailure, isBHAllowed);
            }
        } else {
            this.addNewOrder(order, onSuccess, onFailure, isBHAllowed);
        }
    },

    /**
     * Given new order, find if already exist, so if really new we create a new one, if are existing one, we will change it.
     * @param {Object} Single order
     * @param {Boolean} avoidBatchs
     */
    addNewSingleOrder(singleOrder, onSuccess, onFailure, isBHAllowed, layout) {
        const positionTank = TankStore.getSingleTankPosition(singleOrder);

        if (DashboardLayoutStore.isEmpty(positionTank)) {

            if (isBHAllowed) {
                // calculated batch of brewhouse
                singleOrder = BrewhouseStore.recalculateBrewhouseBatchs(singleOrder, 0, layout);
                if (singleOrder.volume && singleOrder.brewhouseMessageError) {
                    // enter here means that the brew house is full
                    if (onFailure) {
                        onFailure(singleOrder.brewhouseMessageError);
                    } else {
                        Notification.show(singleOrder.brewhouseMessageError);
                    }
                    return;
                }
            } else {
                singleOrder.isBHAllowed = false; // to indicated to lastOrderUpdate of dashboard that is not need to calculate brewhouse batchs
            }

            //Add order to local cache.
            let orderSaved = DashboardServiceCache.addSingleOrder(singleOrder);
            const tankTypeConfig = ContentUtils.getTankTypeConfiguration(_.get(singleOrder, 'singleTank.type'));
            positionTank.i = orderSaved.id + _.get(tankTypeConfig, 'postFix');

            DashboardLayoutStore.push(positionTank);

            // Schedule operation
            DraftSchedule.addOrder(orderSaved);

            // Indicate last order in order to jump later
           this.updateLastOrderCreated(orderSaved);
           this.updateLastOrderUpdated(orderSaved);

            // Trigger and Notify the new Order
            DashboardAction.newOrder(orderSaved);
            Notification.show(t('notification_new_order_saved'), 'success');
            if (onSuccess) {
                onSuccess();
            }
            // we need delay the mark order, in order to get time
            // to the order which insert the node in the dashboard.
            // NOTE: note try to do that during Dashboard.drawOrders
            // because will be affected drag and drop operation.
            setTimeout(() => {
                this.markOrder(_.get(orderSaved, 'id'));
            }, 1000);
        } else {
            Notification.show(t('notification_the_position_is_taken'), 'error');
        }
    },

    removeOneDaySingleOrder(order, dataClicked) {
        if (order) {
            // remove the task that was clicked
            const dateClickedText = DateUtils.toDateText(dataClicked);
            const singleTasks = _.get(order, 'singleTasks');
            const singleTasksFiltered = _.filter(singleTasks, task => task.date !== dateClickedText);
            order.singleTasks = singleTasksFiltered || [];

            // decrement on day the rest of the task
            order = this.decrementTaskOneDay(order, 'singleTasks', dataClicked, false);
            order = this.decrementDateOneDay(order, 'toDate');
        }
        return order;
    },

    /**
     * Validate if given order have a properly dates, and if all validation pass, create a new order.
     * Update layout of dashboard and trigger the proper element.
     * @param order
     */
    addNewOrder(order, onSuccess, onFailure, isBHAllowed) {
        var positionBriteTank,
            positionFermenterTank,
            orderSaved;

        if (!OrderUtils.checkDateOfOrder(order)) {
            return;
        }
        positionBriteTank = TankStore.getBriteTankPosition(order);
        positionFermenterTank = TankStore.getFermenterTankPosition(order);

        if (DashboardLayoutStore.isEmptyPositions(order, positionBriteTank, positionFermenterTank)) {

            if (isBHAllowed) {
                // calculated batch of brewhouse
                order = BrewhouseStore.recalculateBrewhouseBatchs(order);
                if (order.volume && order.brewhouseMessageError) {
                    // enter here means that the brew house is full
                    if (onFailure) {
                        onFailure(order.brewhouseMessageError);
                    } else {
                        Notification.show(order.brewhouseMessageError);
                    }
                    return;
                }
            } else {
                order.isBHAllowed = false; // to indicated to lastOrderUpdate of dashboard that is not need to calculate brewhouse batchs
            }

            // Add order to local cache.
            orderSaved = DashboardServiceCache.addOrder(order);

            // Update layout
            positionBriteTank.i = orderSaved.id + '_BT';
            positionFermenterTank.i = orderSaved.id;
            DashboardLayoutStore.push(positionBriteTank);
            DashboardLayoutStore.push(positionFermenterTank);

            // Schedule operation
            DraftSchedule.addOrder(orderSaved);

            this.updateLastOrderCreated(orderSaved);
            this.updateLastOrderUpdated(orderSaved);

            // Trigger and Notify the new Order
            DashboardAction.newOrder(orderSaved);
            Notification.show(t('notification_new_order_saved'), 'success');
            if (onSuccess) {
                onSuccess();
            }
            // we need delay the mark order, in order to get time
            // to the order which insert the node in the dashboard.
            // NOTE: note try to do that during Dashboard.drawOrders
            // because will be affected drag and drop operation.
            setTimeout(() => {
                this.markOrder(_.get(order, 'id'));
            }, 1000);
        } else {
            Notification.show(t('notification_the_position_is_taken'), 'error');
        }
    },

    /**
     * Remove given order from the cache, and schedule this operation in order to sync
     * with the server states.
     * @param order
     */
    removeOrder(order) {
        this.updateLastOrderUpdated(null);
        this.updateLastOrderCreated(null);
        DashboardServiceCache.removeOrder(order.id);
        DraftSchedule.removeOrder(order);
        DashboardAction.removedOrder();
        Notification.show(t('notification_order_deleted'), 'success');
    },

    /**
     * Change the given order from the cache, and schedule this operation in order to sync
     * with the server this dashboard state.
     * @param order
     */
    changeOrder(order, offsetDays, onSuccess, onFailure, isBHAllowed) {
        var positionBriteTank,
            positionFermenterTank;

        if (!OrderUtils.checkDateOfOrder(order)) {
            return;
        }

        positionBriteTank = TankStore.getBriteTankPosition(order);
        positionFermenterTank = TankStore.getFermenterTankPosition(order);
        if (DashboardLayoutStore.isEmptyPositions(order, positionBriteTank, positionFermenterTank)) {
            // calculated batch of brewhouse
            if (isBHAllowed) {
                order = BrewhouseStore.recalculateBrewhouseBatchs(order, offsetDays);
                if (order.volume && order.brewhouseMessageError) {
                    // enter here means that the brew house is full
                    if (onFailure) {
                        onFailure(order.brewhouseMessageError);
                    } else {
                        Notification.show(order.brewhouseMessageError);
                    }
                    return;
                }
            }

            this.updateLastOrderUpdated(order);
            this.markOrder(_.get(order, 'id'));
            DashboardLayoutStore.updateLayoutPosition(positionFermenterTank, positionBriteTank);

            DashboardServiceCache.changeOrder(order);
            DraftSchedule.changeOrder(order);
            DashboardAction.changeOrder(order);
            Notification.show(t('notification_order_changed'), 'success');
            if (onSuccess) {
                onSuccess();
            }
            // we need delay the mark order, in order to get time
            // to the order which insert the node in the dashboard.
            // NOTE: note try to do that during Dashboard.drawOrders
            // because will be affected drag and drop operation.
            setTimeout(() => {
                this.markOrder(_.get(order, 'id'));
            }, 1000);
        }
    },

    changeOrderLight(order) {
        const positionBriteTank = TankStore.getBriteTankPosition(order);
        const positionFermenterTank = TankStore.getFermenterTankPosition(order);
        DashboardLayoutStore.updateLayoutPosition(positionFermenterTank, positionBriteTank);
        this.updateLastOrderUpdated(order);
        DashboardServiceCache.changeOrder(order);
        DraftSchedule.changeOrder(order);
        DashboardAction.changeOrder(order); 
    },

    addOneDayBelow(order, tankClicked, dateClicked) {
        if (order && order.isSingle) {
            order = this._addOneDayBelowSingleOrder(order, dateClicked);
            if (!order) {
                return false;
            } else {
                this._applySingleOrderChange(order, true);
                return true;
            }
        } else {
            order = this._addOneDayBelowBrewOrder(order, tankClicked, dateClicked);
            if (!order) {
                return false;
            } else {
                this._applyBrewOrderChange(order, true);
                return true;
            }
        }
    },

    addOneDayAbove(order, tankClicked, dateClicked) {
        if (order && order.isSingle) {
            order = this._addOneDayAboveSingleOrder(order, dateClicked);
            if (!order) {
                return false;
            } else {
                this._applySingleOrderChange(order, true);
                return true;
            }
        } else {
            order = this._addOneDayAboveBrewOrder(order, tankClicked, dateClicked);
            if (!order) {
                return false;
            } else {
                this._applyBrewOrderChange(order, true);
                return true;
            }
        }
    },

    removeOneDay(order, tankClicked, dateClicked) {
        if (OrderUtils.hasOneDaySingleOrder(order) || OrderUtils.hasOneDayFermenterSide(order) || OrderUtils.hasOneDayPackagingSide(order)) {
            Notification.show(t('notification_must_have_one_day'));
            return false;
        }

        if (order && order.isSingle) {
            order = OrderStore.removeOneDaySingleOrder(order, dateClicked);
            if (!order) {
                return false;
            } else {
                this._applySingleOrderChange(order, true);
                return true;
            }
        } else {
            order = this._removeOneDayBrewOrder(order, tankClicked, dateClicked);
            if (!order) {
                return false;
            } else {
                this._applyBrewOrderChange(order, true);
                return true;
            }
        }
    },

    moveTask(order, tankClicked, dateClicked, offset) {
        const isSingle = _.get(order, 'isSingle'),
            tankType = _.get(tankClicked, 'type');

        order = this._moveTask(order, dateClicked, tankType, isSingle, offset);

        if (!order) {
            return false;
        }

        if (isSingle) {
            this._applySingleOrderChange(order, true);
            return true;
        } else {
            this._applyBrewOrderChange(order, true);
            return true;
        }
    },

    _moveTask(order, dataClicked, tankType, isSingle, offsetDays) {
        const nextDateClickedText = DateUtils.toDateText(DateUtils.addDays(dataClicked, offsetDays));
        const dateClickedText = DateUtils.toDateText(dataClicked);
        const taskFieldName = isSingle ? 'singleTasks' : ContentUtils.getTankTypeConfiguration(tankType).tasksFieldName;
        const tasks = _.get(order, taskFieldName, []);

        if (!OrderUtils.isOutOfRangeNextTaskPosition(order, nextDateClickedText, isSingle, tankType)) {
            return false;
        }

        // find the current task clicked
        let taskClicked = _.find(tasks, task => task.date === dateClickedText);

        if (taskClicked) {
            // exchange dates between old and new dates
            taskClicked = this._updateTasksDates(tasks, taskClicked, nextDateClickedText);
        }

        // update task type
        order = this._updateTasksType(order, taskFieldName);

        return order;
    },

    _updateTasksDates(tasks, taskClicked, nextDateClickedText) {
        // search if exist a task in the same place that we need to move current task clicked.
        let taskToBeReplaced = _.find(tasks, task => task.date === nextDateClickedText);

        if (taskToBeReplaced) {
            // exchanges dates between the current task date and the task which will be in the next position
            const taskClickedCloned = _.cloneDeep(taskClicked);
            const taskToBeReplacedCloned = _.cloneDeep(taskToBeReplaced);

            // change to task to be replace to task clicked
            taskToBeReplaced.date = taskClickedCloned.date;

            // change task clicked to task to be replashould calculate enough items purchased but don't arrive in timeced
            taskClicked.date = taskToBeReplacedCloned.date;
        } else {
            // just only change the current task date with the the next empty position.
            taskClicked.date = nextDateClickedText;
        }

        // add default end hour product
        taskClicked = OrderUtils.overrideDefaultEndHourToProductsOfTask(taskClicked, nextDateClickedText);

        return taskClicked;
    },

    _removeOneDayBrewOrder(order, tankClicked, dataClicked) {
        if (order) {
            const tankType = _.get(tankClicked, 'type');

            if (tankType === Constants.TYPE_TANK_FERMENTER) {
                const isEnoughTankAvailable = this._checkAvailabilityDecrementOneDay(order, order.briteTank);

                if (!isEnoughTankAvailable) {
                    Notification.show('Not enough space to resize packaging', 'error');
                    return null;
                }

                // remove the task that was clicked
                const dateClickedText = DateUtils.toDateText(dataClicked);
                const tasks = _.get(order, 'fermenterTasks');
                const tasksFiltered = _.filter(tasks, task => task.date !== dateClickedText);
                order.fermenterTasks = tasksFiltered || [];

                // decrement on day the rest of the task
                order = this.decrementTaskOneDay(order, 'fermenterTasks', dataClicked, false);
                order = this.decrementDateOneDay(order, 'filtrationDate');
            } else {
                // remove the task that was clicked
                const dateClickedText = DateUtils.toDateText(dataClicked);
                const tasks = _.get(order, 'packagingTasks');
                const tasksFiltered = _.filter(tasks, task => task.date !== dateClickedText);
                order.packagingTasks = tasksFiltered || [];

                // decrement on day the rest of the task
                order = this.decrementTaskOneDay(order, 'packagingTasks', dataClicked, false);
                order = this.decrementDateOneDay(order, 'orderDate');
            }
        }
        return order;
    },

    _addOneDayBelowBrewOrder(order, tankClicked, dateClicked) {
        return this._addDayBrewOrder(order, tankClicked, dateClicked, true);
    },

    _addOneDayAboveBrewOrder(order, tankClicked, dateClicked) {
        return this._addDayBrewOrder(order, tankClicked, dateClicked, false);
    },

    _addOneDayBelowSingleOrder(order, dateClicked) {
        return this._addDaySingleOrder(order, dateClicked, true);
    },

    _addOneDayAboveSingleOrder(order, dateClicked) {
        return this._addDaySingleOrder(order, dateClicked, false);
    },

    _addDayBrewOrder(order, tankClicked, dateClicked, isExcludeDateClickedToBeUpdated) {
        let isEnoughTankAvailable = false;
        const tankType = _.get(tankClicked, 'type');

        if (tankType === Constants.TYPE_TANK_FERMENTER) {
            // This is the spacial case only of fermenting section, we need to check both dates tank, because in case
            // to increment 1 day in fermenting section we need to increase also de packaging side 1 day too.
            // This double check is to prevent to lose existing packaging tasks.
            isEnoughTankAvailable = this._checkAvailabilityIncrementOneDay(order, order.fermenterTank);

            if (!isEnoughTankAvailable) {
                Notification.show(t('notification_not_enough_space_to_add_day_fermenting_section'), 'error');
                return null;
            }

            isEnoughTankAvailable = this._checkAvailabilityIncrementOneDay(order, order.briteTank);

            if (!isEnoughTankAvailable) {
                Notification.show(t('notification_not_enough_space_to_add_day_packaging_section'), 'error');
                return null;
            }

            // increment 1 day all task
            order = this._incrementTasksOneDay(order, 'fermenterTasks', dateClicked, isExcludeDateClickedToBeUpdated);
            order = this._incrementTasksOneDay(order, 'packagingTasks', dateClicked, false);

            // increment 1 day last date
            order = this._incrementDateOneDay(order, 'filtrationDate');
            order = this._incrementDateOneDay(order, 'orderDate');

            // update task type
            order = this._updateTasksType(order, 'fermenterTasks');

        } else if (tankType === Constants.TYPE_TANK_BRITE_TANK) {

            isEnoughTankAvailable = this._checkAvailabilityIncrementOneDay(order, order.briteTank);

            if (!isEnoughTankAvailable) {
                Notification.show(t('notification_not_enough_space_to_add_day_packaging_section'), 'error');
                return null;
            }

            // increment 1 day all task
            order = this._incrementTasksOneDay(order, 'packagingTasks', dateClicked, isExcludeDateClickedToBeUpdated);

            // increment 1 day last date
            order = this._incrementDateOneDay(order, 'orderDate', false);

            // update task type
            order = this._updateTasksType(order, 'packagingTasks');
        }

        return order;
    },

    _addDaySingleOrder(order, dateClicked, isExcludeDateClickedToBeUpdated) {
        const isEnoughTankAvailable = this._checkSingleOrderAvailabilityIncrementOneDay(order, order.singleTank);

        if (!isEnoughTankAvailable) {
            Notification.show(t('notification_not_enough_space_to_add_day'), 'error');
            return null;
        }

        // increment 1 day all task
        order = this._incrementTasksOneDay(order, 'singleTasks', dateClicked, isExcludeDateClickedToBeUpdated);

        // increment 1 day last date
        order = this._incrementDateOneDay(order, 'toDate', false);

        return order;
    },

    _incrementDateOneDay(order, lastDateField) {
        return this._changeDate(order, lastDateField, 1);
    },

    decrementDateOneDay(order, lastDateField) {
        return this._changeDate(order, lastDateField, -1);
    },

    decrementTaskOneDay(order, fieldName, dateClicked, isExcludeDateClickedToBeUpdated) {
        return this._changeTask(order, fieldName, dateClicked, isExcludeDateClickedToBeUpdated, -1);
    },

    _incrementTasksOneDay(order, fieldName, dateClicked, isExcludeDateClickedToBeUpdated) {
        return this._changeTask(order, fieldName, dateClicked, isExcludeDateClickedToBeUpdated, 1);
    },

    _changeDate(order, lastDateField, offsetDay) {
        const lastDate = DateUtils.toDate(order[lastDateField]);
        order[lastDateField] = DateUtils.addDaysTextBasicFormat(lastDate, offsetDay);
        return order;
    },

    _changeTask(order, fieldName, dateClicked, isExcludeDateClickedToBeUpdated, offsetDay) {
        const tasks = _.get(order, fieldName);

        // starting to separate in two groups of task:
        // The First group: is the group of task after date clicked which need to be updated.
        // The Second group: is the group of task which not need to be updated because is before to date clicked
        if (tasks && _.get(tasks, 'length') > 0) {

            // First group (need update)
            const taskAfterClickedChanged = this._updateTasks(order, tasks, dateClicked, offsetDay, isExcludeDateClickedToBeUpdated);

            // Second group (not need update)
            const taskBeforeClicked = _.filter(tasks, task => {
                if (isExcludeDateClickedToBeUpdated) {
                    // which means was excluded to be updated, so need to be included to second group to non be updated
                    return DateUtils.isBeforeOrEquals(task.date, DateUtils.toDateText(dateClicked));
                } else {
                    return DateUtils.isBefore(task.date, DateUtils.toDateText(dateClicked));
                }
            });

            // combined both groups
            const allTaskUpdated = _.concat(taskBeforeClicked, taskAfterClickedChanged);

            // recalculate all of types of tasks
            const allTaskTypeUpdated = _.map(allTaskUpdated, task => OrderUtils.updateTaskType(order, task));

            // updating order tasks with combined groups
            order = _.set(order, fieldName, allTaskTypeUpdated);
        }
        return order;
    },

    _updateTasks(order, tasks, dateClicked, offsetDay, isExcludeDateClickedToBeUpdated) {
        // filter the set of task that will need to be updated
        const datesAftertDateClicked = _.filter(tasks, task => {
            if (isExcludeDateClickedToBeUpdated) {
                return DateUtils.isAfter(task.date, DateUtils.toDateText(dateClicked));
            } else {
                return DateUtils.isAfterOrEquals(task.date, DateUtils.toDateText(dateClicked));
            }
        });

        // update task in terms of dates and types
        const taskAfterClickedChanged = _.map(datesAftertDateClicked, task => {
            task.date = DateUtils.addDaysTextBasicFormat(DateUtils.toDate(task.date), offsetDay);
            return task;
        });

        return taskAfterClickedChanged;
    },

    _updateTasksType(order, taskFieldName) {
        const tasks = _.get(order, taskFieldName, []);
        const tasksUpdated = _.map(tasks, task => OrderUtils.updateTaskType(order, task));
        order[taskFieldName] = tasksUpdated;
        return order;
    },

    _checkAvailabilityIncrementOneDay(order, tankClicked) {
        return this._checkAvailabilityInLastDate(order, tankClicked, 1);
    },

    _checkAvailabilityDecrementOneDay(order, tankClicked) {
        return this._checkAvailabilityInLastDate(order, tankClicked, -1);
    },

    _checkSingleOrderAvailabilityIncrementOneDay(order, tankClicked) {
        return this._checkSingleOrderAvailabilityInLastDate(order, tankClicked, 1);
    },

    _checkSingleOrderAvailabilityDecrementOneDay(order, tankClicked) {
        return this._checkSingleOrderAvailabilityInLastDate(order, tankClicked, -1);
    },

    _checkAvailabilityInLastDate(order, tankClicked, offsetDay) {
        let position, lastDateOrder;
        const tankType = _.get(tankClicked, 'type');

        if (tankType === Constants.TYPE_TANK_FERMENTER) {
            position = OrderUtils.toFermenterTankPosition(order, ChronologycStore.getCurrentFrom());
            lastDateOrder = order.brewDate;

        } else if (tankType === Constants.TYPE_TANK_BRITE_TANK) {
            position = OrderUtils.toBriteTankPosition(order, ChronologycStore.getCurrentFrom());
            lastDateOrder = order.filtrationDate;
        }

        // update position with the user interaction
        // Add 1 day in order to really know if an extra day added
        position.y = ChronologycStore.getYFromDate(lastDateOrder) + offsetDay;
        position.x = tankClicked.column;

        return DashboardLayoutStore.isEmpty(position);
    },

    _checkSingleOrderAvailabilityInLastDate(order, tankClicked, offsetDay) {
        let position, lastDateOrder;

        position = OrderUtils.toSingleTankPosition(order, ChronologycStore.getCurrentFrom());
        lastDateOrder = order.fromDate;

        // update position with the user interaction
        // Add 1 day in order to really know if an extra day added
        position.y = ChronologycStore.getYFromDate(lastDateOrder) + offsetDay;
        position.x = tankClicked.column;

        return DashboardLayoutStore.isEmpty(position);
    },

    _applySingleOrderChange(order, nonDispatchAction) {
        let singlePosition = TankStore.getSingleTankPosition(order);
        DashboardLayoutStore.updateSingleLayoutPosition(singlePosition);
        // Indicate last order in order to jump later
        this.updateLastOrderUpdated(order);
        this.markOrder(_.get(order, 'id'));

        DashboardServiceCache.changeOrder(order);
        DraftSchedule.changeOrder(order);
        if (!nonDispatchAction) {
            DashboardAction.changeOrder(order);
        }
    },

    _applyBrewOrderChange(order, nonDispatchAction) {
        const positionBriteTank = TankStore.getBriteTankPosition(order);
        const positionFermenterTank = TankStore.getFermenterTankPosition(order);
        // Indicate last order in order to jump later
        this.updateLastOrderUpdated(order);
        this.markOrder(_.get(order, 'id'));

        DashboardLayoutStore.updateLayoutPosition(positionFermenterTank, positionBriteTank);
        DashboardServiceCache.changeOrder(order);
        DraftSchedule.changeOrder(order);
        if (!nonDispatchAction) {
            DashboardAction.changeOrder(order);
        }
    },

    /**
     * Apply change of the given order without execute Commons validation in the order.
     * @param order
     * @private
     */
    _changeOrderWithoutCheck(order, offsetDays = 0, isBatchingProcess, layout = DashboardLayoutStore.getLayout(), isResizing, onSuccess, onFailure) {
        if (OrderUtils.checkDateOfOrder(order)) {

            if (!isResizing && _.get(order, 'batchs.length', 0) > 0) {
                // validate if have batchs, because toggle off on brewhouse not make brew batchs
                order = BrewhouseStore.recalculateBrewhouseBatchsWithLayout(order, offsetDays, layout, isBatchingProcess);
                if (order.volume && order.brewhouseMessageError) {
                    // enter here means that the brew house is full
                    if (onFailure) {
                        onFailure(order.brewhouseMessageError);
                    } else {
                        Notification.show(order.brewhouseMessageError);
                    }
                    DashboardAction.forceRenderDashboard();
                    return;
                }
            }

            const positionBriteTank = TankStore.getBriteTankPosition(order);
            const positionFermenterTank = TankStore.getFermenterTankPosition(order);
            const batchsPositions = BrewhouseStore.getBatchPositions(order);

            DashboardLayoutStore.updateLayoutPositions(
              positionFermenterTank,
              positionBriteTank,
              batchsPositions
            );

            this.updateLastOrderUpdated(order);
            this.markOrder(_.get(order, 'id'));

            DashboardServiceCache.changeOrder(order);
            DraftSchedule.changeOrder(order);
            DashboardAction.changeOrder(order);
            if (onSuccess) {
                onSuccess();
            }
        }
    },

    /**
     * Apply change of the given order without execute Commons validation in the order.
     * @param order
     */
    changeSingleOrderWithoutCheck(order, onSuccess, onFailure, isBHAllowed, layout) {
        if (OrderSingleStore.checkDateOfSingleOrder(order)) {

            if (isBHAllowed) {
                // calculated batch of brewhouse
                order = BrewhouseStore.recalculateBrewhouseBatchsWithLayout(order, 0, layout);
                if (order.volume && order.brewhouseMessageError) {
                    // enter here means that the brew house is full
                    if (onFailure) {
                        onFailure(order.brewhouseMessageError);
                    } else {
                        Notification.show(order.brewhouseMessageError);
                    }
                    return;
                }
            } else {
                order.isBHAllowed = false; // to indicated to lastOrderUpdate of dashboard that is not need to calculate brewhouse batchs
            }

            const singlePosition = TankStore.getSingleTankPosition(order);
            const batchsPositions = BrewhouseStore.getBatchPositions(order);

            DashboardLayoutStore.updateLayoutPositions(singlePosition, batchsPositions);

            // Indicate last order in order to jump later
            this.updateLastOrderUpdated(order);
            this.markOrder(_.get(order, 'id'));

            DashboardServiceCache.changeOrder(order);
            DraftSchedule.changeOrder(order);
            DashboardAction.changeOrder(order);

            if (onSuccess) {
                onSuccess();
            }
        }
    },

    _changeOrderWithoutCheckBrewingSection(order) {
        if (OrderUtils.checkDateOfOrder(order)) {

            const positionBriteTank = TankStore.getBriteTankPosition(order),
                positionFermenterTank = TankStore.getFermenterTankPosition(order),
                batchsPositions = BrewhouseStore.getBatchPositions(order);

            DashboardLayoutStore.updateLayoutPositions(positionFermenterTank, positionBriteTank, batchsPositions);
            this.updateLastOrderUpdated(order);
            this.markOrder(_.get(order, 'id'));

            DashboardServiceCache.changeOrder(order);
            DraftSchedule.changeOrder(order);
            DashboardAction.changeOrder(order);
        }
    },

    changeSingleOrderWithoutCheckBrewingSection(order, onSuccess) {
        if (OrderSingleStore.checkDateOfSingleOrder(order)) {

            const singlePosition = TankStore.getSingleTankPosition(order);
            const batchsPositions = BrewhouseStore.getBatchPositions(order);

            DashboardLayoutStore.updateLayoutPositions(singlePosition, batchsPositions);

            // Indicate last order in order to jump later
            this.updateLastOrderUpdated(order);
            this.markOrder(_.get(order, 'id'));

            DashboardServiceCache.changeOrder(order);
            DraftSchedule.changeOrder(order);
            DashboardAction.changeOrder(order);

            if (onSuccess) {
                onSuccess();
            }
        }
    },

    /**
     * Given the position origin and destination, will be upgrade the order
     * with the new postion. For that, will be execute all the validation of the order,
     * like availability of tanks, dates consistency of the order, etc.
     *
     * @param {Object} positionNew is the destination position.
     * @param {Object} positionOld is the origin position.
     * @private
     * @return {Object}
     */
    _changeOrderFromNewPosition(positionOld, positionNew, isBatchingProcess = false, layout = DashboardLayoutStore.getLayout()) {
        const order = this.getSafeOrder(_.get(positionNew, 'i'));
        
        if (!order) return;
        
        if (order.isSingle) {
            return this._changeSingleOrder(positionOld, positionNew, order, isBatchingProcess, layout);
        } else {
            return this._changeOrder(positionOld, positionNew, order, isBatchingProcess, layout);
        }
    },

    _changeSingleOrder(positionOld, positionNew, order, isBatchingProcess, layout = DashboardLayoutStore.getLayout()) {
        try {
            let newFromDate,
              newToDate,
              isDragging = true,
              orderVolume = ContentUtils.getNumber(_.get(order, 'volume', 0));
    
            // Should not do save nothing if the same position
            if (ContentUtils.isSameObject(positionOld, positionNew, propsToValiadte)) {
                return;
            }
    
            if (!this._isProperlyTypeOfTankDestination(positionNew)) {
                Notification.show(t('dashboard_notification_order_can_not_moved_differen_type_tank'));
                DashboardAction.forceRenderDashboard(); // needed to get back the position after the second time to retry move wrong
                return order;
            }
    
            // NOTE:
            // 1 - Only validate the volume of the order that have volume higher than zero.
            // 2 - Only validate the volume of the orders, it doesn't include batch of BH, (because each of batch from BH have only a part of total volume of the order)
            if (!ContentUtils.isBH(positionNew.i) && !ContentUtils.isBHBatch(positionNew.i) && orderVolume > 0 && !ContentUtils.isBHBatch(positionNew.i) && !BrewhouseStore.isProperlyVolumeOfTankDestination(positionNew, orderVolume)) {
                // enter here means that try to move the position to another tank that don't have volume enough
                Notification.show(t('dashboard_notification_volume_capacity_insufficient'));
                DashboardAction.forceRenderDashboard(); // needed to get back the position after the second time to retry move wrong
                return order;
            }
    
            if (DashboardLayoutStore.isEmpty(positionNew)) {
                newFromDate = ChronologycStore.getFirstDate(positionNew).date;
                newToDate = ChronologycStore.getLastDate(positionNew).date;
                order.singleTank = DashboardServiceCache.getTankByColumn(positionNew.x);
                order.singleTankId = order.singleTank.id;
        
                if (positionNew.h !== positionOld.h) {
                    // enter here means that we are resizing vertical order movement
                    isDragging = false;
                }
        
                order.fromDate = newFromDate || order.fromDate;
                order.toDate = newToDate || order.toDate;
                order = OrderSingleStore.recalculateSingleOrderTasks(order, isDragging, positionOld, positionNew);
        
                const offsetDays = OrderUtils.getVerticalOffeset(positionOld, positionNew);
        
                // calculated batch of brewhouse
                if (_.get(order, 'batchs.length', 0) > 0) {
                    // Only recalculate batch's of orders that already have batch before.
                    order = BrewhouseStore.recalculateBrewhouseBatchsWithLayout(order, offsetDays, layout, isBatchingProcess);
                    if (order.volume && order.brewhouseMessageError) {
                        // enter here means that the brew house is full
                        Notification.show(order.brewhouseMessageError);
                        return;
                    }
                }
        
                this.changeSingleOrderWithoutCheck(order);
        
                // we need delay the mark order, in order to get time
                // to the order which insert the node in the dashboard.
                // NOTE: note try to do that during Dashboard.drawOrders
                // because will be affected drag and drop operation.
                setTimeout(() => {
                    this.markOrder(_.get(order, 'id'));
                }, 1000);
            } else {
                Notification.show(t('notification_no_space_available'));
            }
            return order;
        } catch(error) {
            console.error('OrderStore._changeSingleOrder: ', error);
            throw error;
        }
    },


    _changeOrder(oldPosition, newPosition, order, isBatchingProcess, nextLayout) {
        try {
            let isBriteTank,
              newBrewDate,
              newFiltrationDate,
              newOrderDate,
              newPositionSiblingTank,
              differedDays,
              orderId = ContentUtils.cleanId(newPosition.i),
              nextTankAvailable,
              isDragging = true,
              orderVolume = +_.get(order, 'volume', 0),
              offsetResizing = 0;
    
            const backupLayout = _.cloneDeep(DashboardLayoutStore.getLayout());
    
            if (ContentUtils.isSameObject(oldPosition, newPosition, propsToValiadte)) {
                // enter here means that the old and new position is the same, so do nothing
                return;
            }
    
            if (!this._isProperlyTypeOfTankDestination(newPosition)) {
                // enter here means that try to move the position to another incompatible section
                Notification.show(t('dashboard_notification_order_can_not_moved_differen_type_tank'));
                DashboardAction.forceRenderDashboard(); // needed to get back the position after the second time to retry move wrong
                return null;
            }
    
            const offsetDays = newPosition.y - oldPosition.y;
    
            if (orderVolume > 0 && !BrewhouseStore.isProperlyVolumeOfTankDestination(newPosition, orderVolume)) {
                // enter here means that try to move the position to another tank that don't have volume enough
                Notification.show(t('dashboard_notification_volume_capacity_insufficient'));
                DashboardAction.forceRenderDashboard(); // needed to get back the position after the second time to retry move wrong
                return null;
            }
    
            if (DashboardLayoutStore.isEmpty(newPosition)) {
        
                isBriteTank = ContentUtils.isBT(newPosition.i);
        
                if (!order) {
                    Notification.show(t('notification_label_the_order') + orderId + t('notification_order_not_available'));
                    return;
                }
        
                if (isBriteTank) {
                    // enter here means the current position is brite tank
                    newFiltrationDate = ChronologycStore.getFirstDate(newPosition).date;
                    newOrderDate = ChronologycStore.getLastDate(newPosition).date;
                    order.briteTank = DashboardServiceCache.getTankByColumn(newPosition.x);
                    order.briteTankId = order.briteTank.id;
                    order.orderDate = newOrderDate;
                    order.filtrationDate = newFiltrationDate;
                    // try to calculate the sibling tank
                    newPositionSiblingTank = TankStore.getPositionFermenterFromBriteTank(order, newPosition, oldPosition);
                } else {
                    // enter here means the current position is fermenter tank
                    newBrewDate = ChronologycStore.getFirstDate(newPosition).date;
                    newFiltrationDate = ChronologycStore.getLastDate(newPosition).date;
                    order.fermenterTank = DashboardServiceCache.getTankByColumn(newPosition.x);
                    order.fermenterTankId = order.fermenterTank.id;
                    order.brewDate = newBrewDate;
                    order.filtrationDate = newFiltrationDate;
                    // try to calculate the sibling tank
                    newPositionSiblingTank = TankStore.getPositionBriteTankFromFermenter(order, newPosition, oldPosition);
                    if (newPositionSiblingTank && newPositionSiblingTank.h <= 0) {
                        Notification.show(t('notification_the_transfer_date_cannot_after_packaging_date'), 'error');
                        return;
                    }
                }
        
                if (newPosition.h !== oldPosition.h) {
                    // means the current position is vertical resizing, not dragging
                    isDragging = false;
                    // days offset that was changed the filtration date or packaging date
                    offsetResizing = newPosition.h - oldPosition.h;
                }
        
                // If change the vertical position we need to calculate the brew date or order date, depend the tank that was moved.
                if (newPosition.y !== oldPosition.y || newPosition.h !== oldPosition.h) {
                    differedDays = newPosition.y - oldPosition.y;
                    if (isBriteTank) {
                        newBrewDate = ChronologycStore.getDateFromPositionY(newPositionSiblingTank.y).date;
                    } else {
                        newOrderDate = ChronologycStore.getDateFromPositionY(newPositionSiblingTank.y + newPositionSiblingTank.h - 1).date;
                    }
                }
        
                if (!newPositionSiblingTank && isBriteTank) {
                    // Enter here means that the filtration tank was out of range and the change position came from brite tank.
                    newPositionSiblingTank = TankStore.getFermenterTankPositionManual(orderId, order.fermenterTank.column, newBrewDate, newFiltrationDate, order.batchNumber, order.isSingle);
                }
        
                if (isSiblingTankPositionAvailable(newPositionSiblingTank, differedDays)) {
                    // Enter here means the new position of sibling tank is already taken, for that, we try to follow another empty position in the same day in another tank.
                    if (isBriteTank) {
                        nextTankAvailable = TankStore.getNextFermenterTankAvailable(DateUtils.toDate(newBrewDate), DateUtils.toDate(newFiltrationDate), orderId, order.volume);
                    } else {
                        nextTankAvailable = TankStore.getNextBriteTankAvailable(DateUtils.toDate(newFiltrationDate), DateUtils.toDate(newOrderDate), orderId, order.volume);
                    }
                    if (nextTankAvailable) {
                        // Enter here means that original position for sibling tank was taken but could be found another one for the same day.
                        if (isBriteTank) {
                            order.fermenterTank = nextTankAvailable;
                            order.fermenterTankId = nextTankAvailable.id;
                        } else {
                            order.briteTank = nextTankAvailable;
                            order.briteTankId = nextTankAvailable.id;
                        }
                    } else {
                        // Enter here means that the new position of sibling tank was taken, we try to found another tank but all the tank for this day are taken too.
                        Notification.show(t('notification_no_space_available'));
                        DashboardLayoutStore.setLayout(backupLayout);
                        return;
                    }
                }
        
                order.brewDate = newBrewDate || order.brewDate;
                order.orderDate = newOrderDate || order.orderDate;
                order.filtrationDate = newFiltrationDate;
                order.batchNumber = _.get(newPosition, 'batchNumber');
                order = this.recalculateOrderTasks(order, isDragging, offsetDays, offsetResizing, isBriteTank);
        
                this._changeOrderWithoutCheck(order, offsetDays, isBatchingProcess, nextLayout, !isDragging);
        
            } else {
                DashboardLayoutStore.setLayout(backupLayout);
                Notification.show(t('notification_no_space_available'));
                return;
            }
            return order;
         } catch(error) {
          console.error('OrderStore._changeOrder: ', error);
          throw error;
        }
    },

    /**
     * Change the order which have another order asociated by Bach Number.
     *
     * <b>NOTE:</b> this method is full support by Single and Combined order compliance.
     *
     * @param oldPosition
     * @param newPosition
     * @param stickyOrdersWithoutOldPosition
     * @param offsetY
     * @return {Array}
     * @private
     */
    _changeOrderFromNewPositionWithStickyOrder(newPosition, offsetY) {
        let result = [];

        let backupLayout = _.cloneDeep(DashboardLayoutStore.getLayout());

        // this layout will be share the same state of positions along to all the batching processing operation
        let nextLayout = _.cloneDeep(DashboardLayoutStore.getLayout());

        // process all position of given batch number and return the result of this process
        const resultProcess = this._processPositions(newPosition, nextLayout, offsetY);

        // all the new position already processed
        const newPositions = _.map(resultProcess.positionsPerformed, position => position.newPosition);

        // ask if same position is out of range of dashboard.
        if (this._isSamePositionOutOfRange(newPositions)) {
            Notification.show(t('notification_order_outside_visible_area'), 'warning');
            DashboardLayoutStore.setLayout(backupLayout);
            return result;
        }

        // Only apply all changes when ALL position are available's and passed all validations!!
        if (resultProcess.isAllPositionAvailables) {
            this._processPendingUpdatesOrders(resultProcess);

            if (resultProcess.positionsPerformed && resultProcess.positionsPerformed.length > 0) {
                // update layout position with new positions
                _.forEach(_.map(resultProcess.positionsPerformed, position => position.newPosition), newPosition => {
                    DashboardLayoutStore.updateLayoutPositionBySpecificId(newPosition);
                });

                const isBatchingProcess = true;
                // apply the whole chages of each positions computed because all validation was success!
                _.forEach(resultProcess.positionsPerformed, position => {
                    // calculate dates given old and new position already computed.
                    result.push(this._changeOrderFromNewPosition(position.oldPosition, position.newPosition, isBatchingProcess));
                });
            }
            backupLayout = null;
            nextLayout = null;
            // notify that all operation are already executed
            DashboardAction.executeBatchOperationsEnd(_.get(newPosition, 'batchNumber'));
            return result;
        } else {
            // enter here means that some of position don't have space enough, so all batch could not be moved, we restore the layout as the beginning
            Notification.show('No space available', 'error');
            DashboardLayoutStore.setLayout(backupLayout);
            return result;
        }
    },

    _processPendingUpdatesOrders(resultProcess) {
        // execute all pending update order operations
        if (_.get(resultProcess, 'pendingUpdateOrders.length', 0) > 0) {
            for (const pendingUpdateOrder of resultProcess.pendingUpdateOrders) {
                pendingUpdateOrder();
            }
        }
    },

    _processPositions(newPosition, nextLayout, offsetY) {
        // the whole position of batch
        const batchAllPositions = _.filter(nextLayout, position => position.batchNumber === newPosition.batchNumber);

        let resultProcess = {
            isAllPositionAvailables: true
        };

        let positionPerformedIds = [];
        let positionsPerformed = [];
        let pendingUpdateOrders = [];
        let result = {
            isProcessOK: true
        };

        const batchNumber = newPosition.batchNumber;
        const brewDates = this._getAllPositionByBatchnumberWithoutBrewhouse(batchNumber, nextLayout);
        const brewhousesByY = this._getAllBrewhouseBatchsByY(batchNumber, nextLayout);

        if (!this._checkIfOrderHaveEnoughBatchsAvailablesOnBrewDates(brewhousesByY, batchNumber, offsetY, brewDates, nextLayout, offsetY)) {
            Notification.show(t('notification_not_enough_brewhouse_turns_available'));
            result.isProcessOK = false;

            return result;
        }

        let isAllPositionAvailables = true;

        // process each position of whole batch order
        for (const oldPosition of batchAllPositions) {
            const nextPosition = _.cloneDeep(oldPosition);

            // apply offset to position
            nextPosition.y = nextPosition.y + offsetY;

            // only apply x and h which position that are currently dragged
            if (oldPosition.i === newPosition.i) {
                nextPosition.x = newPosition.x;
                nextPosition.h = newPosition.h;
            }

            // check if already processed this order
            if (OrderUtils.isAlreadyPerformedGivenPosition(positionPerformedIds, nextPosition)) {
                // enter here means the position are already processed, so we continue with the next
                continue;
            } else {

                if (ContentUtils.isBHBatch(nextPosition.i)) {
                    // process batch position just to be sure that will valid the next position.
                    result = this._processBatchPosition(nextPosition, oldPosition, nextLayout, positionPerformedIds, positionsPerformed, offsetY);
                } else {
                    // process all siblings of combined order and the single position in chase of singles.
                    result = this._processPosition(nextPosition, oldPosition, nextLayout, positionPerformedIds, positionsPerformed, offsetY);
                }

                if (result.isProcessOK) {
                    if (result.doUpdateOrder) {
                        pendingUpdateOrders.push(result.doUpdateOrder);
                    }
                    nextLayout = result.nextLayout;
                } else {
                    // if it has at least one position that it doesn't available, should not say that
                    // all positions are availables
                    isAllPositionAvailables = false;
                    break;
                }
            }
        }

        resultProcess.isAllPositionAvailables = isAllPositionAvailables;

        // Remove all unneeded positions such us:
        // When we have a combined order, eg: Fermenting and Packaging position,
        // only keep one of them, and the another will be re-calculated in
        // the `_changeOrder` method. Also all the batch position from brewhouse
        // section, will be removed as well, because will be calculated again into
        // the method `_changeOrder` too.
        const positionsPerformedUpdated = OrderUtils.filterUnneededPositions(positionsPerformed);

        resultProcess.positionsPerformed = positionsPerformedUpdated;
        resultProcess.pendingUpdateOrders = pendingUpdateOrders;
        return resultProcess;
    },

    _checkIfOrderHaveEnoughBatchsAvailablesOnBrewDates(batchsByY, batchNumber, offsetY, brewDates, nextLayout) {
        const amountBrewhouseBatch = _.filter(TankStore.getTanks(), tank => tank.type === 'BH').length;

        let amountOfBatchAvailableByDate = {};
        let amountOfBatchNonAvailableByDate = {};

        // amount of non availables batch by date
        for (const brewDatePosition of brewDates) {
            const date = _.get(ChronologycStore.getDateFromPositionY(brewDatePosition.y), 'date');
            const positionYUpdated = brewDatePosition.y + offsetY;
            const dateUpdated = DateUtils.addDaysTextBasicFormat(date, offsetY);
            const batchOccupied = _.filter(nextLayout, position => position.y === positionYUpdated && ContentUtils.isBHBatch(position.i) && position.batchNumber !== batchNumber);

            amountOfBatchNonAvailableByDate[dateUpdated] = _.get(batchOccupied, 'length', 0);
            amountOfBatchAvailableByDate[dateUpdated] = amountBrewhouseBatch - _.get(batchOccupied, 'length', 0);
        }

        for (const brewDatePosition of brewDates) {
            const positionYUpdated = brewDatePosition.y + offsetY;
            const brewDate = _.get(ChronologycStore.getDateFromPositionY(positionYUpdated), 'date');

            const amountOfBatchNeedToTake = _.get(batchsByY[brewDatePosition.y], 'length', 0);
            if (amountOfBatchAvailableByDate[brewDate] < amountOfBatchNeedToTake) {
                return false;
            }
        }

        return true;
    },

    _getAllBrewhouseBatchsByY(batchNumber, nextLayout) {
        const allBatchOfBrewhouse = _.filter(nextLayout, position => position.batchNumber === batchNumber && ContentUtils.isBHBatch(position.i));
        const groupBrewhouseBatchByOrder = _.groupBy(allBatchOfBrewhouse, position => position.y);
        return groupBrewhouseBatchByOrder;
    },

    _getAllPositionByBatchnumberWithoutBrewhouse(batchNumber, nextLayout) {
        const allBatchOfBrewhouse = _.filter(nextLayout, position => position.batchNumber === batchNumber && !ContentUtils.isBH(position.i) && !ContentUtils.isBT(position.i));

        const groupBrewhouseBatchByOrder = _.groupBy(allBatchOfBrewhouse, position => ContentUtils.cleanId(position.i));
        let brewhouseBatch = [];

        for (const orderId in groupBrewhouseBatchByOrder) {
            const batchs = groupBrewhouseBatchByOrder[orderId];
            const positionYBrewDate = _.get(_.minBy(batchs, 'y'), 'y');
            const brewDateBrewhouseBatchs = _.filter(batchs, batch => batch.y === positionYBrewDate);
            brewhouseBatch.push(brewDateBrewhouseBatchs);
        }

        return _.flatten(brewhouseBatch);
    },

    _processPosition(nextPosition, oldPosition, nextLayout, positionPerformedIds = [], positionsPerformed = [], offsetY) {

        let result = {
            isProcessOK: true,
            nextLayout
        };

        if (DashboardLayoutStore.isEmpty(nextPosition, nextLayout)) {
            // enter here once the new position have an empty space their new location
            result.doUpdateOrder = () => {
                this._updateOrder(nextPosition, offsetY);
            };
            this._updatePositionPerformed(positionPerformedIds, positionsPerformed, nextPosition, oldPosition);
            nextLayout = this._updateLayoutWithPosition(nextLayout, nextPosition);
            result.nextLayout = nextLayout;
            return result;
        } else {
            // enter here means that is occupied next position wich an order with another bach number
            let nextPositionAvailable = this._getNextPositionAvailable(nextPosition, offsetY, nextLayout);

            if (nextPositionAvailable) {
                result.doUpdateOrder = () => {
                    // enter here once the next available position have an empty, so we performed it
                    this._updateOrder(nextPositionAvailable, offsetY);
                };

                this._updatePositionPerformed(positionPerformedIds, positionsPerformed, nextPositionAvailable, oldPosition);
                nextLayout = this._updateLayoutWithPosition(nextLayout, nextPositionAvailable);
                result.nextLayout = nextLayout;
                return result;
            } else {
                // cancel all batch operation because one of the positions could not be re-located.
                result.isProcessOK = false;
                return result;
            }
        }
    },

    _processBatchPosition(nextPosition, oldPosition, nextLayout, positionPerformedIds = [], positionsPerformed = [], offsetY) {
        let result = {
            isProcessOK: true,
            nextLayout
        };
        const order = this.getSafeOrder(_.get(nextPosition, 'i'));
        const orderUpdated = OrderUtils.updateDates(order, offsetY);
        const startDate = ContentUtils.getOrderField(orderUpdated, 'startDate');
        const batchAvailables = BrewhouseStore.getBatchAvailables(orderUpdated, startDate, offsetY, nextLayout);

        if (_.get(batchAvailables, 'length', 0) === 0) {
            result.isProcessOK = false;

            return result;
        } else {
            const batchElected = OrderUtils.electBatchAvailable(batchAvailables, nextPosition);
            const batchElectedPosition = _.get(batchElected, 'position');

            // update this current position
            nextLayout = this._updateLayoutWithPosition(nextLayout, batchElectedPosition, nextPosition.i);

            this._updatePositionPerformed(positionPerformedIds, positionsPerformed, nextPosition, oldPosition);

            result.nextLayout = nextLayout;

            return result;
        }
    },

    _updateOrderOfSiblingPositions(nextLayout, order) {
        const siblingPositions = _.filter(nextLayout, position => position.batchNumber === order.batchNumber && ContentUtils.isBHBatch(position.i));

        for (const positionsUpdated of siblingPositions) {
            nextLayout = this._updateLayoutWithPosition(nextLayout, positionsUpdated);
        }

        return nextLayout;
    },

    _getNextPositionAvailable(newPosition, offsetY, layout) {
        const order = this.getSafeOrder(_.get(newPosition, 'i'));

        // enter here which means the position could be or FV or BT position.
        return this._getNextFermentingOrPackagingPositionAvailable(order, newPosition, offsetY, layout);
    },

    _getNextFermentingOrPackagingPositionAvailable(order, newPosition, offsetDays, layout) {
        const tank = TankStore.getTankByColumn(newPosition.x);
        const startDate = DateUtils.toDate(ContentUtils.getOrderField(order, 'startDate', tank.type, offsetDays));
        const endDate = DateUtils.toDate(ContentUtils.getOrderField(order, 'endDate', tank.type, offsetDays));
        const tanksStatus = TankStore.getAvailabilityTanks(startDate, endDate, tank.type, order.id, layout);
        const tanksAvailables = _.filter(tanksStatus, tank => tank.isAvailable && +tank.volume >= +(order.volume || 0));

        if (_.get(tanksAvailables, 'length', 0) === 0) {
            // enter here which means that not found tank available so we need to stop batching movement process
            return null;
        }

        for (const tank of tanksAvailables) {
            const positionNeedCheck = {
                h: newPosition.h,               // use the same
                x: _.get(tank, 'column'),       // use the same
                y: newPosition.y,               // re-located tank
                i: newPosition.i,               // use the same
                batchNumber: _.get(order, 'batchNumber'),  // use the same
                w: newPosition.w,
                static: newPosition.static,
                maxW: newPosition.maxW,
                minW: newPosition.minW
            };

            // tankName field to avoid conditional between tank types
            const tankNameField = _.get(ContentUtils.getTankTypeConfiguration(tank.type), 'orderTankName');
            // update tank which new tank available
            order[tankNameField] = tank;
            //order = this.updateDates(order, offsetDays);
            // update serialized order from new position available

            return positionNeedCheck;
        }
        // if we are here, means don't have tank available to given position, so we need to stop batching movement process
        return null;
    },

    _updateLayoutWithPosition(layout, newPosition, oldId) {
        const targetId = oldId || newPosition.i;
        newPosition.isRelocated = true;
        layout = _.filter(layout, position => position.i !== targetId);
        layout.push(newPosition);
        return layout;
    },

    _updatePositionPerformed(positionPerformedIds = [], positionsPerformed = [], newPosition, oldPosition) {
        // add to list of order ids already performed
        newPosition.isRelocated = true;

        positionPerformedIds.push(newPosition.i);

        positionsPerformed.push({
            oldPosition,
            newPosition
        });
    },

    _updateOrder(newPosition, offsetY) {

        if (!newPosition) {
            return null;
        }

        // update the new position
        let order = this.getSafeOrder(_.get(newPosition, 'i'));

        // update dates
        order = OrderUtils.updateDates(order, offsetY);

        // we don't need to update icon task because, will be changed in the next steps

        DashboardServiceCache.updateOrder(order);

        return newPosition;
    },

    _recalculateOrdersWithNewVerticalPosition(oldPosition, newPosition, offsetY) {
        let result = [],
            isPositionTarget;

        if (DashboardServiceCache.getTankByColumn(oldPosition.x).type !== DashboardServiceCache.getTankByColumn(newPosition.x).type) {
            Notification.show(t('dashboard_notification_order_can_not_moved_differen_type_tank'));
            return result;
        }

        // prepare both type of search.
        const batchNumber = _.get(newPosition, 'batchNumber');
        const orderId = _.get(newPosition, 'i');

        if (OrderUtils.haveSomeProductConfirmedFromOrderId(orderId)) {
            Notification.show(t('notification_confirmed_orders_cannot_moved'));
            return result;
        }

        if (OrderUtils.haveSomeProductConfirmedFromButchNumber(batchNumber)) {
            Notification.show(t('notification_confirmed_orders_cannot_moved_check_orders_with_same_batch_number'));
            return result;
        }

        // by default search by batchNumber, if are undefined, will search by orderId
        if (!batchNumber) {
            isPositionTarget = layoutPosition => _.get(layoutPosition, 'i') === orderId;
        } else {
            isPositionTarget = layoutPosition => _.get(layoutPosition, 'batchNumber') === batchNumber;
        }

        let stickyOrdersWithoutOldPosition = _.filter(DashboardLayoutStore.getLayout(), position => {
            // We need to filter both by ID (only in case of combined order), because then one of them we calculate as new position and the other will calculate
            // from the first one. If we calculate each position (means not filter both), we could have bugs like re-process the same position twice and will
            // produce several bugs when re-calculate the position again.
            return isPositionTarget(position) && ContentUtils.cleanId(position.i) !== ContentUtils.cleanId(oldPosition.i);
        }) || [];


        if (_.get(stickyOrdersWithoutOldPosition, 'length') > 0) {
            // Enter here means that found a sticky orders, so we need to recalculate first their availability and then process with the new position.
            return this._changeOrderFromNewPositionWithStickyOrder(newPosition, offsetY);
        } else {
            // Enter here means that don't have sticky order, so is just normal movement (eg: an combined or single movement order).
            return this._changeOrderFromNewPosition(oldPosition, newPosition);
        }
    },

    _isSamePositionOutOfRange(stickyPositionToUpdate) {
        return _.some(stickyPositionToUpdate, position => ChronologycStore.isPositionOutOfRange(position));
    },

    /**
     * Validate if the column that try to take is compatible with the type of tank that came from.
     * @param {Object} position that will be validated if is fit to the column that try to fill.
     * @return {Boolean}
     */
    _isProperlyTypeOfTankDestination(position) {
        const tankToValidate = _.find(DashboardServiceCache.getTanks(), tank => tank.column === position.x);

        if (tankToValidate) {
            switch (tankToValidate.type) {
                case Constants.TYPE_TANK_FERMENTER:
                    return !ContentUtils.isBH(position.i) && !ContentUtils.isBT(position.i);
                case Constants.TYPE_TANK_BRITE_TANK:
                    return ContentUtils.isBT(position.i);
                case Constants.TYPE_TANK_BREW_HOUSE:
                    return ContentUtils.isBH(position.i);
            }
        } else {
            Notification.show(t('notification_column_of_the_new_position') + position.x + t('notification_do_not_exist'), 'error');
            return false;
        }
    },

    _changeBatchPosition(oldPosition, newPosition) {
        let order = this.getSafeOrder(_.get(newPosition, 'i')),
            daysBeetweenBrewingAndFermenting;

        if (OrderUtils.haveSomeProductConfirmedByPosition(oldPosition)) {
            Notification.show(t('notification_confirmed_orders_cannot_moved'));
            return order;
        }

        if (DashboardLayoutStore.isEmpty(newPosition)) {
            let brewDate;

            if (order.isSingle) {
                brewDate = _.get(order, 'fromDate');
                daysBeetweenBrewingAndFermenting = DateUtils.getDaysBetween(order.fromDate, order.toDate);
            } else {
                brewDate = _.get(order, 'brewDate');
                daysBeetweenBrewingAndFermenting = DateUtils.getDaysBetween(brewDate, order.filtrationDate);
            }

            const newBrewDate = _.get(ChronologycStore.getDateFromPositionY(newPosition.y), 'date');
            const daysOffset = DateUtils.getDaysBetween(newBrewDate, brewDate);

            if (daysOffset < -Math.abs(daysBeetweenBrewingAndFermenting) || daysOffset > 1) {
                Notification.show(t('notification_all_brewhouse_must_be_completed_within_fermenting_period'));
                return order;
            }

            if (daysOffset < -2) {
                Notification.show(t('notification_warning_late_wort_addition'), 'warning');
            }

            const turn = DashboardServiceCache.getTankByColumn(newPosition.x);
            const orderBatchs = _.get(order, 'batchs', []);
            const oldBatch = _.find(orderBatchs, batch => {
                return batch.date === _.get(ChronologycStore.getDateFromPositionY(oldPosition.y), 'date') && _.get(batch, 'turn.column') === oldPosition.x;
            });

            if (!oldBatch) {
                // do nothing because this method was called wrong.
                return order;
            }

            // create new batch
            let batchUpdated = _.cloneDeep(oldBatch);
            batchUpdated.turn = turn;
            batchUpdated.turnId = _.get(turn, 'id');
            batchUpdated.date = newBrewDate;
            batchUpdated.position = newPosition;

            let batchs = _.filter(orderBatchs, batch => {
                return !(_.get(batch, 'turn.column') === _.get(oldBatch, 'turn.column') &&
                    _.get(batch, 'date') === _.get(oldBatch, 'date'));
            });

            // update batch changed
            batchs.push(batchUpdated);

            // Ask if at least one batch exist in brew day or one day before to brew day
            if (!BrewhouseStore.isBrewdateAvailable(batchs, brewDate)) {
                // enter here means that don't exist batch for brewDate, so could not possible to change the order
                Notification.show(t('notification_brewhouse_must_have_at_least_one_brewdate'));
                return order;
            }

            // update batchs of the order
            order.batchs = batchs;

            // save change
            if (order.isSingle) {
                this.changeSingleOrderWithoutCheckBrewingSection(order);
            } else {
                this._changeOrderWithoutCheckBrewingSection(order);
            }
        } else {
            Notification.show(t('notification_brewhouse_at_full_capacity_on_selected_date'));
            return order;
        }
    },

    haveSomeConfirmedProductAtTheEndOfTheOrderBySectionForCombinedOrder(order, sectionType) {
        if (Constants.FERMENTING_SECTION_NAME === sectionType) {

          const fermenterProducts = OrderUtils.getAllFermentingProductsFromOrder(order);
          const fermenterDateProducts = _.filter(fermenterProducts, product => DateUtils.isEqual(product.endDate, order.filtrationDate));
          return _.some(fermenterDateProducts, product => product.isConfirmed);

        } else  if (Constants.PACKAGING_SECTION_NAME === sectionType) {

          const packagingProducts = OrderUtils.getAllPackagingProductsFromOrder(order);
          const packagingDateProducts = _.filter(packagingProducts, product => DateUtils.isEqual(product.endDate, order.orderDate));
          return _.some(packagingDateProducts, product => product.isConfirmed);

        } else {

            return false;
        }
    },

    haveSomeConfirmedProductAtTheEndOfTheOrderForSingleOrder(order) {
          const products = OrderUtils.getAllProductsFromOrder(order);
          const productsDate = _.filter(products, product => DateUtils.isEqual(product.endDate, order.toDate));
          return _.some(productsDate, product => product.isConfirmed);
    },

    isResizeAllowed(order, positionsInfo) {
        const {start, stop} = positionsInfo;
        
        if (!start || !stop) {
            return false;
        }
        
        const bottomOldPosition = start.y + start.h;
        const bottomNewPosition = stop.y + stop.h;
        // const positionSection = ContentUtils.isBT(stop.i) ? Constants.PACKAGING_SECTION_NAME : Constants.FERMENTING_SECTION_NAME;

        const oldDate = _.get(ChronologycStore.getDateFromPositionY(bottomOldPosition - 1), 'date');
        const newDate = _.get(ChronologycStore.getDateFromPositionY(bottomNewPosition - 1), 'date');

        let from, to, isResizeDirectionTop;
        if (DateUtils.isBefore(oldDate, newDate)) {
            isResizeDirectionTop = false;
            from = oldDate;
            to = newDate;
        } else {
            isResizeDirectionTop = true;
            from = newDate;
            to = oldDate;
        }


        if (this.haveSomeConfirmedProductOutsideOfOrderAfterResizing(order, to, from, isResizeDirectionTop)) {
            return false;
        }

        return true;
    },

    haveSomeConfirmedProductOutsideOfOrderAfterResizing(order, previous, next, isResizeDirectionTop) {
        const isNotResized = previous === next;
        if (!isResizeDirectionTop || isNotResized) {
            return false;
        }
        const previousDate = DateUtils.toDateText(previous);
        const nextDate = DateUtils.toDateText(next);
        const products = OrderUtils.getAllProductsFromOrder(order);
        if (ContentUtils.isNotEmptyArray(products)) {
            const filteredProducts = _.filter(products, product => {
                const end = _.get(product, 'endDate');
                const productDate = DateUtils.toDateText(end);
                return DateUtils.isBeforeOrEquals(productDate, previousDate) && DateUtils.isAfter(productDate, nextDate);
            });
            return _.some(filteredProducts, product => _.get(product, 'isConfirmed'));
        }
        return false;
    },
    
    existSomeProductConfirmed(order, oldDate, newDate) {
        let isResizeDirectionTop;
        if (DateUtils.isBefore(oldDate, newDate)) {
            isResizeDirectionTop = false;
        } else {
            isResizeDirectionTop = true;
        }
        return this.haveSomeConfirmedProductIntoCurrentOrNextDate(order, oldDate, newDate, isResizeDirectionTop);
    },
    
    haveSomeConfirmedProductIntoCurrentOrNextDate(order, previous, next, isResizeDirectionTop) {
        const isNotResized = previous === next;
        if (!isResizeDirectionTop || isNotResized) {
            return false;
        }
        const previousDate = DateUtils.toDateText(previous);
        const nextDate = DateUtils.toDateText(next);
        const products = OrderUtils.getAllProductsFromOrder(order);
        if (ContentUtils.isNotEmptyArray(products)) {
            const filteredProducts = _.filter(products, product =>
              _.get(product, 'endDate') === previousDate ||
              _.get(product, 'endDate') === nextDate
            );
            return _.some(filteredProducts, product => _.get(product, 'isConfirmed'));
        }
        return false;
    },
    
    removeChangeListener(call) {
        this.removeListener(CHANGE_EVENT, call);
    },

    addChangeListener(call) {
        this.on(CHANGE_EVENT, call);
    },

    markOrderListener(call) {
        if (call) {
            this._markOrder = call;
        }
    },

    markOrder(orderId) {
        if (this._markOrder) {
            this._markOrder(orderId);
        }
    }
});

module.exports = OrderStore;



/** WEBPACK FOOTER **
 ** ./src/stores/OrderStore.js
 **/